import React, { useState } from "react"
import { useSelector } from "react-redux"
import { useTheme, Typography, Stack, Button } from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { useUserRole, useRemovePartnerAccess } from "#/components/partner-access/hooks/usePartnerAccess"
import { getCarrotLite } from "#/redux/reducers/companyInfo"
import { InviteStatusResult, PartnerInviteStatus } from "#/components/partner-access/partnerAccessTypes"
import { SettingsPageDivider } from "#/pages/account/page"
import { EmailWithChip } from "#/components/EmailVerification/EmailAddressesAccountSettings"
import PartnerAccessRemovalDialog from "./PartnerAccessRemovalDialog"

export function PartnerAccessInformationActive({ inviteData }: { inviteData?: InviteStatusResult }): JSX.Element {
  const isCarrotLite = useSelector(getCarrotLite)
  const { partnerEmail } = useCurrentUser()
  const { isPartnerAccessingAccount } = useUserRole()
  const theme = useTheme()
  const intl = useIntl()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const useRemovePartnerAccessQuery = useRemovePartnerAccess()

  const handleConfirmRemove = () => {
    useRemovePartnerAccessQuery.mutate(undefined, {
      onSuccess: () => {
        setIsDialogOpen(false)
      }
    })
  }

  return inviteData?.status === PartnerInviteStatus.ACTIVE && !isPartnerAccessingAccount ? (
    <>
      <SettingsPageDivider />
      <Typography variant="h2" marginBottom={(theme) => theme.spacing(theme.tokens.spacing.xs)}>
        <FormattedMessage defaultMessage="Partner email address" />
      </Typography>
      <Stack spacing={theme.spacing(theme.tokens.spacing.md)}>
        <Typography>
          {!isCarrotLite
            ? intl.formatMessage({
                defaultMessage:
                  "Your partner can sign in to this account with their own email address and password. They can access educational resources, benefit details, and more, but they won't be able to edit your account information, access financial areas, or see your in-app messages. They may be able to see personal information like gender identity."
              })
            : intl.formatMessage({
                defaultMessage:
                  "Your partner can sign in to this account with their own email address and password. They can access educational resources, benefit details, and more, but they won't be able to edit your account information or see your in-app messages. They may be able to see personal information like gender identity."
              })}
        </Typography>
        <EmailWithChip
          email={partnerEmail}
          chipLabel={intl.formatMessage({
            defaultMessage: "partner sign-in"
          })}
        />
        <>
          <Typography fontStyle="italic">
            <FormattedMessage defaultMessage="Need to change your partner’s sign-in? Remove their current access, update their email address under Partner information and invite them again using their new email address." />
          </Typography>
          <Button
            id="remove-partner-access"
            sx={{ inlineSize: "fit-content" }}
            variant="outlined"
            color="secondary"
            onClick={() => setIsDialogOpen(true)}
          >
            <FormattedMessage defaultMessage="Revoke partner access" />
          </Button>
        </>
      </Stack>

      <PartnerAccessRemovalDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirmRemove={handleConfirmRemove}
        isLoading={useRemovePartnerAccessQuery.isLoading}
      />
    </>
  ) : null
}
